import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { XCircleIcon } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { orderQueryOptions, useCancelOrderRequestMutation, useOrderSuspenseQuery } from '@/api/accommodation/hooks'
import { HttpError } from '@/api/http'
import { unitFullName } from '@/lib/unit'
import { formatRequestDeadline } from '@/lib/orderRequest'
import { SITE_NAME, noindexMeta } from '@/lib/seo/siteMeta'

// Opened from the "Zrušit žádost" link in the request confirmation e-mail.
export const Route = createFileRoute('/_layout/zadost/$orderNumber/$token')({
  component: OrderRequestPage,
  errorComponent: OrderRequestErrorComponent,
  notFoundComponent: OrderRequestErrorComponent,
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.query({
        ...orderQueryOptions(params.orderNumber),
        staleTime: 'static',
      })
    } catch (error) {
      // A dead link must surface as a real 404, not an SSR 500.
      if (error instanceof HttpError && error.status === 404) {
        throw notFound()
      }
      throw error
    }
  },
  head: () => ({
    meta: [{ title: `Žádost o rezervaci | ${SITE_NAME}` }, ...noindexMeta()],
  }),
})

function OrderRequestPage() {
  const { orderNumber, token } = Route.useParams()
  const navigate = useNavigate()
  const { data: order } = useOrderSuspenseQuery(orderNumber)
  const cancel = useCancelOrderRequestMutation(orderNumber, token)

  const isOpen = order.state.state === 'requested' && order.request
  const closedMeanwhile = cancel.error instanceof HttpError && cancel.error.status === 400

  const handleCancel = () => {
    if (!window.confirm('Opravdu chcete žádost o rezervaci zrušit?')) return
    cancel.mutate(undefined, {
      onSuccess: () => {
        void navigate({
          to: '/objednavka/$orderNumber',
          params: { orderNumber },
        })
      },
    })
  }

  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16">
      <div className="w-full max-w-md space-y-4 text-center">
        <Typography as="h1" variant="h2">
          Žádost o rezervaci {order.number}
        </Typography>
        {order.location && (
          <p className="text-slate-600">
            {order.unit ? unitFullName(order.location, order.unit) : order.location.name},{' '}
            {format(order.start, 'd. M.')} – {format(order.end, 'd. M. yyyy')}
          </p>
        )}

        {isOpen && !closedMeanwhile ? (
          <>
            <p className="text-slate-600">
              Hostitel odpoví do {formatRequestDeadline(order.request!.expiresAt)}. Dokud neodpoví, můžete žádost zrušit
              — blokaci {order.payment?.total.toLocaleString('cs-CZ')} Kč na kartě hned uvolníme.
            </p>
            {cancel.isError && (
              <p className="text-sm text-red-600">Žádost se nepodařilo zrušit, zkuste to prosím znovu.</p>
            )}
            <Button variant="outline" disabled={cancel.isPending} onClick={handleCancel}>
              {cancel.isPending ? 'Rušíme žádost…' : 'Zrušit žádost'}
            </Button>
          </>
        ) : (
          <>
            <p className="text-slate-600">Žádost už není otevřená, hostitel ji mezitím vyřídil.</p>
            <Button asChild>
              <Link to="/objednavka/$orderNumber" params={{ orderNumber }}>
                Zobrazit objednávku
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

function OrderRequestErrorComponent() {
  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <XCircleIcon className="mb-4 h-12 w-12 text-red-500" />
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">Odkaz nefunguje</h1>
      <p className="mb-6 max-w-sm text-slate-500">
        Zkuste prosím otevřít odkaz z e-mailu znovu, nebo nám napište na info@matevolno.cz.
      </p>
      <Button asChild>
        <Link to="/">Zpět na hlavní stránku</Link>
      </Button>
    </div>
  )
}
